import React from 'react';
import { Box, Grid, Typography, Card, CardContent } from '@mui/material';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const EngagementAnalytics = ({ data }) => {
  const { activity = [], satisfaction = 0, nps = 0 } = data || {};
  
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>Member Engagement</Typography>
      
      <Grid container spacing={3}>
        {/* Satisfaction Score Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Satisfaction Score</Typography>
              <Typography variant="h4">{satisfaction.toFixed(1)}%</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Overall member satisfaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* NPS Score Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">NPS Score</Typography>
              <Typography variant="h4">{nps}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Net Promoter Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Active Members Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Active Members</Typography>
              <Typography variant="h4">
                {Math.round(data?.activePercentage || 0)}%
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Members active in last 30 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Engagement Score Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Engagement Score</Typography>
              <Typography variant="h4">
                {data?.engagementScore?.toFixed(1) || '0.0'}/10
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Overall engagement rating
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Activity Chart */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Member Activity Over Time</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={activity} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="logins" 
                    stroke="#8884d8" 
                    name="Logins" 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="pageViews" 
                    stroke="#82ca9d" 
                    name="Page Views" 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="interactions" 
                    stroke="#ffc658" 
                    name="Interactions" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Engagement Breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Engagement by Feature</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={data?.featureEngagement || []} 
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="feature" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar 
                    dataKey="usagePercentage" 
                    name="Usage %" 
                    fill="#8884d8" 
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Engagement Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Engagement Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data?.engagementDistribution || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                  >
                    {(data?.engagementDistribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EngagementAnalytics;
